
import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  doc,
  onSnapshot,
  query,
  runTransaction,
  serverTimestamp,
  deleteDoc,
} from "firebase/firestore";

import { db } from "../firebase";
import websocket from "../services/websocket";
import "./AdminForexTracking.css";

const STOP_OUT_LEVEL = 50;

const n = (value, fallback = 0) =>
  Number.isFinite(Number(value))
    ? Number(value)
    : fallback;

const cleanSymbol = (value) =>
  String(value || "")
    .replace(/[^A-Za-z]/g, "")
    .toUpperCase();

const money = (value) => {
  const amount = n(value);
  return `${amount < 0 ? "-" : ""}$${Math.abs(amount).toFixed(2)}`;
};

const dateText = (value) => {
  if (!value) return "—";

  const date = value.toDate
    ? value.toDate()
    : new Date(value);

  return Number.isNaN(date.getTime())
    ? "—"
    : date.toLocaleString();
};

// Makadirio ya P/L.
// Kwa baadhi ya Forex pairs na metals, currency conversion inahitajika.
const calculatePnl = (trade, price) => {
  const entry = n(
    trade.openPrice ?? trade.entryPrice
  );

  const units = n(
    trade.units,
    n(trade.lots) * 100000
  );

  if (!entry || !price || !units) return 0;

  const side = String(trade.side || "buy").toLowerCase();

  const difference =
    side === "sell"
      ? entry - price
      : price - entry;

  return Math.round(difference * units * 100) / 100;
};

export default function AdminForexTracking() {
  const [trades, setTrades] = useState([]);
  const [users, setUsers] = useState([]);
  const [prices, setPrices] = useState({});
  const [loading, setLoading] = useState(true);

  const [tab, setTab] = useState("open");
  const [closedView, setClosedView] = useState(false);

  const [search, setSearch] = useState("");
  const [period, setPeriod] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  // =====================================================
  // LOAD ALL FOREX TRADES
  // =====================================================

  useEffect(() => {
    const unsubscribe = onSnapshot(
      query(collection(db, "Forex")),
      (snapshot) => {
        setTrades(
          snapshot.docs.map((item) => ({
            id: item.id,
            ...item.data(),
          }))
        );

        setLoading(false);
        setError("");
      },
      (err) => {
        console.error("Forex listener:", err);
        setError("Imeshindikana kusoma Forex. Kagua Firestore Rules.");
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  // =====================================================
  // LOAD USERS AND THEIR BALANCES
  // =====================================================

  useEffect(() => {
    const unsubscribe = onSnapshot(
      query(collection(db, "users")),
      (snapshot) => {
        setUsers(
          snapshot.docs.map((item) => ({
            id: item.id,
            ...item.data(),
          }))
        );
      },
      (err) => console.error("Users listener:", err)
    );

    return () => unsubscribe();
  }, []);

  const getUser = (username) =>
    users.find((user) => user.username === username);

  // =====================================================
  // LIVE MARKET PRICES
  // =====================================================

  const liveSymbols = useMemo(
    () => [
      ...new Set(
        trades
          .filter(
            (trade) =>
              trade.status === "open" &&
              trade.closed !== true
          )
          .map((trade) => cleanSymbol(trade.symbol))
          .filter(Boolean)
      ),
    ],
    [trades]
  );

  useEffect(() => {
    if (!liveSymbols.length) return undefined;

    websocket.connect();

    const unsubscribe = websocket.onTick((data) => {
      const tick = data?.tick;
      const ohlc = data?.ohlc;

      const rawSymbol = String(
        tick?.symbol ||
          tick?.underlying_symbol ||
          ohlc?.symbol ||
          ohlc?.underlying_symbol ||
          ""
      );

      const price = Number(
        tick?.quote ?? ohlc?.close
      );

      if (
        !rawSymbol.toLowerCase().startsWith("frx") ||
        !Number.isFinite(price) ||
        price <= 0
      ) {
        return;
      }

      const symbol = rawSymbol
        .replace(/^frx/i, "")
        .toUpperCase();

      setPrices((previous) => ({
        ...previous,
        [symbol]: price,
      }));
    });

    liveSymbols.forEach((symbol) => {
      websocket.history(`frx${symbol}`, 30);
    });

    return () => {
      if (typeof unsubscribe === "function") {
        unsubscribe();
      }
    };
  }, [liveSymbols]);

  const currentPrice = (trade) =>
    n(
      prices[cleanSymbol(trade.symbol)] ??
        trade.currentPrice ??
        trade.marketPrice ??
        trade.openPrice ??
        trade.entryPrice
    );

  // =====================================================
  // TRADE GROUPS
  // =====================================================

  const openTrades = useMemo(
    () =>
      trades.filter(
        (trade) =>
          trade.status === "open" &&
          trade.closed !== true
      ),
    [trades]
  );

  const pendingTrades = useMemo(
    () =>
      trades.filter(
        (trade) =>
          trade.status === "pending" &&
          trade.closed !== true
      ),
    [trades]
  );

  const closedTrades = useMemo(
    () =>
      trades.filter(
        (trade) =>
          trade.status === "closed" ||
          trade.closed === true
      ),
    [trades]
  );

  const profitLoss = (trade) => {
    if (
      trade.status === "closed" ||
      trade.closed === true
    ) {
      return n(
        trade.realisedProfitLoss ??
          trade.profitLoss
      );
    }

    return calculatePnl(
      trade,
      currentPrice(trade)
    );
  };

  const floatingPnl = openTrades.reduce(
    (sum, trade) => sum + profitLoss(trade),
    0
  );

  const realisedPnl = closedTrades.reduce(
    (sum, trade) =>
      sum +
      n(
        trade.realisedProfitLoss ??
          trade.profitLoss
      ),
    0
  );

  const usedMargin = openTrades.reduce(
    (sum, trade) =>
      sum + Math.max(0, n(trade.marginRequired)),
    0
  );

  // Hii ni jumla ya balance za users wote.
  // Si Equity sahihi ya kila user mmoja mmoja.
  const allUserBalances = users.reduce(
    (sum, user) =>
      sum + n(user.usdtBalanceOther),
    0
  );

  const estimatedEquity =
    allUserBalances + floatingPnl;

  const estimatedMarginLevel =
    usedMargin > 0
      ? (estimatedEquity / usedMargin) * 100
      : null;

  const rows =
    tab === "open"
      ? openTrades
      : tab === "pending"
      ? pendingTrades
      : closedTrades;

  // =====================================================
  // SEARCH AND FILTER
  // =====================================================

  const visibleTrades = useMemo(() => {
    const term = search.trim().toLowerCase();
    const now = Date.now();

    return rows.filter((trade) => {
      const text = [
        trade.username,
        trade.symbol,
        trade.displaySymbol,
        trade.id,
        trade.tradeId,
        trade.side,
        trade.status,
      ]
        .join(" ")
        .toLowerCase();

      if (term && !text.includes(term)) {
        return false;
      }

      if (
        statusFilter !== "all" &&
        trade.status !== statusFilter
      ) {
        return false;
      }

      if (
        tab === "closed" &&
        period !== "all" &&
        trade.closedAt
      ) {
        const timestamp =
          trade.closedAt.toMillis
            ? trade.closedAt.toMillis()
            : new Date(trade.closedAt).getTime();

        const duration =
          period === "24h"
            ? 86400000
            : period === "7d"
            ? 7 * 86400000
            : 30 * 86400000;

        if (
          timestamp &&
          timestamp < now - duration
        ) {
          return false;
        }
      }

      return true;
    });
  }, [
    rows,
    search,
    statusFilter,
    period,
    tab,
  ]);

  // =====================================================
  // ADMIN CLOSE TRADE + UPDATE BALANCE
  // =====================================================

  const closeTrade = async (trade) => {
    if (busy) return;

    const symbol = cleanSymbol(trade.symbol);
    const price = n(
      prices[symbol] ??
        trade.currentPrice ??
        trade.marketPrice ??
        0
    );

    if (!(price > 0)) {
      setError(
        "Bei ya sasa haipatikani. Trade haijafungwa."
      );
      return;
    }

    const user = getUser(trade.username);

    if (!user) {
      setError(
        `User ${trade.username || ""} hajapatikana.`
      );
      return;
    }

    const result = calculatePnl(trade, price);

    setBusy(trade.id);
    setError("");
    setNotice("");

    try {
      await runTransaction(db, async (transaction) => {
        const tradeRef = doc(db, "Forex", trade.id);
        const userRef = doc(db, "users", user.id);

        const tradeSnapshot =
          await transaction.get(tradeRef);

        const userSnapshot =
          await transaction.get(userRef);

        if (
          !tradeSnapshot.exists() ||
          !userSnapshot.exists()
        ) {
          throw new Error(
            "Trade au user document haipo."
          );
        }

        const latestTrade = tradeSnapshot.data();

        if (
          latestTrade.status === "closed" ||
          latestTrade.closed === true
        ) {
          throw new Error(
            "Trade hii tayari imefungwa."
          );
        }

        const balance = Number(
          userSnapshot.data().usdtBalanceOther
        );

        if (!Number.isFinite(balance)) {
          throw new Error(
            "usdtBalanceOther si namba sahihi."
          );
        }

        transaction.update(tradeRef, {
          status: "closed",
          closed: true,
          closePrice: price,
          currentPrice: price,
          profitLoss: result,
          realisedProfitLoss: result,
          closeReason: "admin_manual",
          closedByAdmin: true,
          closedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });

        transaction.update(userRef, {
          usdtBalanceOther:
            Math.round((balance + result) * 100) / 100,
          updatedAt: serverTimestamp(),
        });
      });

      setNotice(
        `Trade imefungwa. P/L: ${money(result)}`
      );
    } catch (err) {
      console.error("Admin close failed:", err);
      setError(
        err.message || "Imeshindikana kufunga trade."
      );
    } finally {
      setBusy("");
    }
  };

  // =====================================================
  // EDIT TRADE
  // =====================================================

  const startEdit = (trade) => {
    setEditing({
      id: trade.id,
      username: trade.username || "",
      symbol: trade.symbol || "",
      side: trade.side || "buy",
      status: trade.status || "open",
      lots: String(trade.lots ?? ""),
      units: String(trade.units ?? ""),
      openPrice: String(trade.openPrice ?? ""),
      entryPrice: String(trade.entryPrice ?? ""),
      currentPrice: String(trade.currentPrice ?? ""),
      marginRequired: String(
        trade.marginRequired ?? ""
      ),
    });
  };

  const saveEdit = async (event) => {
    event.preventDefault();

    if (!editing || busy) return;

    setBusy(editing.id);
    setError("");

    try {
      await runTransaction(db, async (transaction) => {
        const ref = doc(db, "Forex", editing.id);
        const snapshot = await transaction.get(ref);

        if (!snapshot.exists()) {
          throw new Error("Trade haipo.");
        }

        const oldTrade = snapshot.data();

        if (
          oldTrade.status === "closed" ||
          oldTrade.closed === true
        ) {
          throw new Error(
            "Closed trade haihaririwi hapa ili kulinda audit ya balance."
          );
        }

        transaction.update(ref, {
          username: editing.username.trim(),
          symbol: editing.symbol.trim().toUpperCase(),
          side: editing.side,
          status: editing.status,
          lots: n(editing.lots),
          units: n(editing.units),
          openPrice: n(editing.openPrice),
          entryPrice: n(editing.entryPrice),
          currentPrice: n(editing.currentPrice),
          marginRequired: n(editing.marginRequired),
          updatedAt: serverTimestamp(),
        });
      });

      setEditing(null);
      setNotice("Trade imehaririwa.");
    } catch (err) {
      setError(
        err.message || "Imeshindikana kuhariri trade."
      );
    } finally {
      setBusy("");
    }
  };

  // =====================================================
  // DELETE TRADE PERMANENTLY
  // =====================================================

  const deleteTrade = async (trade) => {
    if (busy) return;

    setBusy(trade.id);
    setError("");

    try {
      await deleteDoc(
        doc(db, "Forex", trade.id)
      );

      setDeleteTarget(null);
      setNotice(
        "Trade imefutwa. Balance haijabadilishwa."
      );
    } catch (err) {
      setError(
        err.message || "Imeshindikana kufuta trade."
      );
    } finally {
      setBusy("");
    }
  };

  // =====================================================
  // USER INTERFACE
  // =====================================================

  return (
    <div className="admin-forex-container">
      <h2>Admin Forex Tracking</h2>

      <div className="aft-summary">
        <div className="aft-summary-card">
          <h3>Open Trades</h3>
          <p>{openTrades.length}</p>
        </div>

        <div className="aft-summary-card">
          <h3>Pending</h3>
          <p>{pendingTrades.length}</p>
        </div>

        <div className="aft-summary-card">
          <h3>Closed</h3>
          <p>{closedTrades.length}</p>
        </div>

        <div className="aft-summary-card">
          <h3>Floating P/L</h3>
          <p className={floatingPnl < 0 ? "negative" : "positive"}>
            {money(floatingPnl)}
          </p>
        </div>

        <div className="aft-summary-card">
          <h3>Realised P/L</h3>
          <p className={realisedPnl < 0 ? "negative" : "positive"}>
            {money(realisedPnl)}
          </p>
        </div>
      </div>

      <section className="aft-margin-panel">
        <h3>Auto Stop Out Formula</h3>

        <p>
          <strong>
            Margin Level (%) = Equity ÷ Used Margin × 100
          </strong>
        </p>

        <p>
          Equity = Balance + Floating P/L
          {" · "}
          Stop Out threshold:{" "}
          <strong>{STOP_OUT_LEVEL}%</strong>
        </p>

        <p>
          Estimated Equity:{" "}
          <strong>{money(estimatedEquity)}</strong>
          {" · "}
          Used Margin:{" "}
          <strong>{money(usedMargin)}</strong>
          {" · "}
          Margin Level:{" "}
          <strong>
            {estimatedMarginLevel === null
              ? "N/A"
              : `${estimatedMarginLevel.toFixed(2)}%`}
          </strong>
        </p>

        <small>
          Hii ni estimate ya jumla tu. Margin Level sahihi
          inapaswa kuhesabiwa kwa kila user kivyake.
          Panel hii inaonyesha formula, si uthibitisho kwamba
          Auto Stop Out inatekelezwa na server.
        </small>
      </section>

      <div className="aft-toolbar">
        <input
          className="aft-search"
          value={search}
          onChange={(event) =>
            setSearch(event.target.value)
          }
          placeholder="Tafuta username, symbol, trade ID..."
        />

        <select
          value={statusFilter}
          onChange={(event) =>
            setStatusFilter(event.target.value)
          }
        >
          <option value="all">Status zote</option>
          <option value="open">Open</option>
          <option value="pending">Pending</option>
          <option value="closed">Closed</option>
          <option value="expired">Expired</option>
          <option value="cancelled">Cancelled</option>
        </select>

        {tab === "closed" && (
          <select
            value={period}
            onChange={(event) =>
              setPeriod(event.target.value)
            }
          >
            <option value="all">Muda wote</option>
            <option value="24h">Last 24 hours</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
          </select>
        )}
      </div>

      <div className="aft-tabs">
        <button
          className={tab === "open" ? "active" : ""}
          onClick={() => setTab("open")}
        >
          Open ({openTrades.length})
        </button>

        <button
          className={tab === "pending" ? "active" : ""}
          onClick={() => setTab("pending")}
        >
          Pending ({pendingTrades.length})
        </button>

        <button
          className={tab === "closed" ? "active" : ""}
          onClick={() => {
            if (tab !== "closed") {
              setTab("closed");
              setClosedView(true);
            } else {
              setClosedView((value) => !value);
            }
          }}
        >
          Closed View {closedView ? "▴" : "▾"} (
          {closedTrades.length})
        </button>
      </div>

      {error && (
        <div className="aft-error">{error}</div>
      )}

      {notice && (
        <div className="aft-notice">
          {notice}
          <button onClick={() => setNotice("")}>
            ×
          </button>
        </div>
      )}

      {loading ? (
        <p className="aft-message">
          Inapakia trades...
        </p>
      ) : tab === "closed" && !closedView ? (
        <p className="aft-message">
          Bonyeza Closed View kuonyesha trades zilizofungwa.
        </p>
      ) : (
        <div className="aft-table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Username</th>
                <th>Trade ID</th>
                <th>Symbol</th>
                <th>Side</th>
                <th>Status</th>
                <th>Lots</th>
                <th>Open Price</th>
                <th>Market/Close</th>
                <th>Margin</th>
                <th>P/L</th>
                <th>Balance</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              {visibleTrades.map((trade) => {
                const user = getUser(trade.username);

                const isClosed =
                  trade.status === "closed" ||
                  trade.closed === true;

                return (
                  <tr key={trade.id}>
                    <td>{trade.username || "—"}</td>
                    <td>{trade.tradeId || trade.id}</td>
                    <td>
                      {trade.symbol ||
                        trade.displaySymbol ||
                        "—"}
                    </td>
                    <td>
                      {String(trade.side || "—").toUpperCase()}
                    </td>
                    <td>
                      <span
                        className={`aft-status ${trade.status || ""}`}
                      >
                        {trade.status || "unknown"}
                      </span>
                    </td>
                    <td>{n(trade.lots).toFixed(2)}</td>
                    <td>
                      {n(
                        trade.openPrice ??
                          trade.entryPrice
                      ).toFixed(5)}
                    </td>
                    <td>
                      {n(
                        isClosed
                          ? trade.closePrice
                          : currentPrice(trade)
                      ).toFixed(5)}
                    </td>
                    <td>{money(trade.marginRequired)}</td>
                    <td
                      className={
                        profitLoss(trade) < 0
                          ? "negative"
                          : "positive"
                      }
                    >
                      {money(profitLoss(trade))}
                    </td>
                    <td>
                      {user
                        ? money(user.usdtBalanceOther)
                        : "User haipo"}
                    </td>
                    <td>
                      {dateText(
                        isClosed
                          ? trade.closedAt
                          : trade.createdAt
                      )}
                    </td>
                    <td>
                      <div className="aft-actions">
                        {!isClosed &&
                          trade.status === "open" && (
                            <button
                              className="aft-close"
                              disabled={Boolean(busy)}
                              onClick={() =>
                                closeTrade(trade)
                              }
                            >
                              {busy === trade.id
                                ? "Inafunga..."
                                : "Close Market"}
                            </button>
                          )}

                        <button
                          className="aft-edit"
                          disabled={Boolean(busy)}
                          onClick={() => startEdit(trade)}
                        >
                          Edit
                        </button>

                        <button
                          className="aft-delete"
                          disabled={Boolean(busy)}
                          onClick={() =>
                            setDeleteTarget(trade)
                          }
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}

              {visibleTrades.length === 0 && (
                <tr>
                  <td colSpan="13">
                    Hakuna trades zinazolingana na filter.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* EDIT MODAL */}
      {editing && (
        <div
          className="aft-modal-backdrop"
          onClick={() => setEditing(null)}
        >
          <form
            className="aft-editor"
            onSubmit={saveEdit}
            onClick={(event) => event.stopPropagation()}
          >
            <h3>Edit Trade</h3>
            <p>Trade ID: {editing.id}</p>

            <div className="aft-fields">
              {[
                "username",
                "symbol",
                "lots",
                "units",
                "openPrice",
                "entryPrice",
                "currentPrice",
                "marginRequired",
              ].map((key) => (
                <label key={key}>
                  {key}
                  <input
                    value={editing[key]}
                    onChange={(event) =>
                      setEditing((previous) => ({
                        ...previous,
                        [key]: event.target.value,
                      }))
                    }
                    required={["username", "symbol"].includes(key)}
                  />
                </label>
              ))}

              <label>
                Side
                <select
                  value={editing.side}
                  onChange={(event) =>
                    setEditing((previous) => ({
                      ...previous,
                      side: event.target.value,
                    }))
                  }
                >
                  <option value="buy">Buy</option>
                  <option value="sell">Sell</option>
                </select>
              </label>

              <label>
                Status
                <select
                  value={editing.status}
                  onChange={(event) =>
                    setEditing((previous) => ({
                      ...previous,
                      status: event.target.value,
                    }))
                  }
                >
                  <option value="open">Open</option>
                  <option value="pending">Pending</option>
                </select>
              </label>
            </div>

            <p className="aft-warning">
              Kufanya edit ya lots, units, bei au margin
              kunaweza kubadilisha hesabu za trade.
              Closed trades hazihaririwi kwenye fomu hii.
            </p>

            <div className="aft-modal-actions">
              <button
                type="button"
                onClick={() => setEditing(null)}
              >
                Cancel
              </button>

              <button
                type="submit"
                disabled={Boolean(busy)}
              >
                Save Changes
              </button>
            </div>
          </form>
        </div>
      )}

      {/* DELETE CONFIRMATION */}
      {deleteTarget && (
        <div
          className="aft-modal-backdrop"
          onClick={() => setDeleteTarget(null)}
        >
          <div
            className="aft-confirm"
            onClick={(event) => event.stopPropagation()}
          >
            <h3>Futa trade kabisa?</h3>

            <p>
              {deleteTarget.username} ·{" "}
              {deleteTarget.symbol} · {deleteTarget.id}
            </p>

            <p className="aft-warning">
              Hii itafuta document ya Forex kabisa.
              Balance haitabadilishwa na history haitafutwa.
              Kwa audit ya kifedha, soft-delete ni salama zaidi.
            </p>

            <div className="aft-modal-actions">
              <button
                onClick={() => setDeleteTarget(null)}
              >
                Cancel
              </button>

              <button
                className="aft-delete"
                disabled={Boolean(busy)}
                onClick={() => deleteTrade(deleteTarget)}
              >
                Delete Permanently
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
